import axios from "axios";

class SignatureRequestService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl;
  }

  /**
   * @author Alan Aguilar
   * @description Get signature Requests by user
   * @date 29-07-2020
   * @param {Number} status
   * @param {Number} limit
   * @param {Number} orderStatusId
   * @param {String} token
   * @returns {Object}
   * @memberof SignatureRequestService
   */
  async getSignatureRequests(status, limit, token) {
    let response = await axios.get(
      `${this.apiUrl
      }/sign-requests?status=${status}&limit=${limit}&orderStatusId=${1}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Alan Aguilar
   * @description Get signature History Requests by client of the user
   * @date 17-11-2020
   * @param {String} token
   * @returns {Object}
   * @memberof SignatureRequestService
   */
  async getSignatureHistoryRequests(token) {
    let response = await axios.get(`${this.apiUrl}/sign-requests-history`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  /**
   * @author Lucia Alvarado
   * @description Get signature Request by id
   * @date 29-07-2020
   * @param {String} token
   * @param {Object} requestId
   * @returns {Object}
   * @memberof SignatureRequestService
   */
  async getSignatureRequestById(id, token) {
    const resp = await axios
      .get(`${this.apiUrl}/sign-request?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(function (response) {
        return response.data;
      })
      .catch(function (error) {
        return error.response.data;
      });
    return resp;
  }

  /**
   * @author Alan Aguilar
   * @description Create a signRequest in MiFirma-API
   * @date 10-08-2021
   * @param {Object} signRequestBody
   * @param {String} token
   * @returns {Object}
   * @memberof SignatureRequestService
   */
  async createSignatureRequest(signRequestBody, token) {
    let response = await axios.post(
      `${this.apiUrl}/sign-request`,
      signRequestBody,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Alan Aguilar
   * @description Delete a sign request by signRequestId from hibou.legal
   * @date 05-10-2020
   * @param {Number} signStatusId
   * @param {String} comments
   * @param {String} token
   * @returns {Object}
   * @memberof SignatureRequestService
   */
  async deleteSignRequestsById(id, comments, token) {
    let response = await axios.delete(
      `${this.apiUrl}/sign-request?id=${id}&comments=${comments}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Alan Aguilar
   * @description Update a sthe expiration date of a sign request
   * @date 26-10-2020
   * @param {String} id
   * @param {String} expirationDate
   * @param {String} token
   * @returns {Object}
   * @memberof SignatureRequestService
   */
  async updateSignRequestExpirationDate(id, expirationDate, token) {
    let response = await axios.put(
      `${this.apiUrl}/sign-request-expiration-date`,
      {
        id,
        expirationDate,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Alan Aguilar
   * @description Resend the information of a sign request aproved until 7 days after its completed
   * @updatedDate 03-06-2021
   * @param {String} signRequestId
   * @param {String} token
   * @returns {Object}
   * @memberof SignatureRequestService
   */
  async resendSignRequestInformation(signRequestId, token) {
    let response = await axios.post(
      `${this.apiUrl}/sign-request-resend?signRequestId=${signRequestId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Alan Aguilar
   * @description Resend the information of a sign request aproved until 7 days after its completed
   * @updatedDate 03-06-2021
   * @param {String} id
   * @param {String} token
   * @returns {Object}
   * @memberof SignatureRequestService
   */
  async reactivationSignRequestRejected(id, token) {
    let response = await axios.post(
      `${this.apiUrl}/sign-request-reactivation?id=${id}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Converts .csv file to object JSON
   * @date 16-3-2022
   * @param {Form-data} fileCSV
   * @param {String} token
   * @returns {Object}
   * @memberof SignatureRequestService
   */

  async readCsvFile(fileCSV, token) {
    const response = await axios.post(
      `${this.apiUrl}/sign-request-read-csv`,
      fileCSV,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Converts .csv file to object JSON
   * @date 16-3-2022
   * @param {FormData} fileCSV
   * @param {string} token
   * @returns {Promise<import("../types/api.types").ApiResponse<import("../types/signature-request.types").ReadCsvFileResponse[]>>}
   * @memberof SignatureRequestService
   */
  static async readCsvFile(fileCSV, token) {
    const response = await axios.post(
      `${process.env.REACT_APP_MIFIRMA_API}/sign-request-read-csv`,
      fileCSV,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Juan Manuel Lamperti
   * @description Service that generates the flow of signature requests by volume
   * @date 16-3-2022
   * @param {Array} documents
   * @param {arrayJSON} signatureRequest
   * @param {String} token
   * @returns {Object}
   * @memberof SignatureRequestService
   */

  async generateFlowOfSignatureRequestByVolume(signRequestBodyByVolume, token) {
    const response = await axios.post(
      `${this.apiUrl}/sign-request-by-volume`,
      signRequestBodyByVolume,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Edgar escobed
   * @description get informatios about sign request qith QR
   * @date 28/10-2022
   * @param {Array} id
   * @param {Array} hash
   * @param {String} token
   * @returns {import("../types/api.types").ApiResponse<import("../types/qr-signed-document.types").QRSignedDocumentProps>}
   * @memberof SignatureRequestService
   */
  async getSIgngRequesOpenQR(id, hash) {
    let response = await axios.get(
      `${this.apiUrl}/sign-request-open?id=${id}&hash=${hash}`,
      {}
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description Sign Request package
  * @date 12-02-2024
  * @param {String} token
  * @param {Object} signRequestBody
  * @returns {Object}
  * @memberof SignatureRequestService
  */
  async createSignatureRequestPackage(signRequestBody, token) {
    let response = await axios.post(
      `${this.apiUrl}/sign-request-docs-package`,
      signRequestBody,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @author Pedro Antonio
   * @description Get signature Request by id public 
   * @date 29-07-2020
   * @param {String} token
   * @param {Object} requestId
   * @returns {Object}
   * @memberof SignatureRequestService
   */
  async getSignatureRequestByIdPublic(id, email, token) {
    const resp = await axios
      .get(`${this.apiUrl}/sign-request-public?id=${id}&email=${email}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then(function (response) {
        return response.data;
      })
      .catch(function (error) {
        return error.response.data;
      });
    return resp;
  }

  /**
  * @author Edgar Escobedo
  * @description Create a singRequest
  * @date 17-12-2024
  * @param {String} token
  * @param {Object} signRequestBody
  * @returns {Object}
  * @memberof SignatureRequestService
  */
  async createSignRequest(signRequestBody, token) {
    let response = await axios.post(
      `${this.apiUrl}/sign-request-unified`,
      signRequestBody,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
 * @author Edgar Escobedo
 * @description Endpoint para que funcionen las solicitudes de firma anteriores 
 * @date 14-01-25
 * @param {String} token
 * @param {Object} requestId
 * @returns {Object}
 * @memberof SignatureRequestService
 */
  async getOldSignatureRequestById(id, token) {
    const resp = await axios
      .get(`${this.apiUrl}/old-sign-request?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(function (response) {
        return response.data;
      })
      .catch(function (error) {
        return error.response.data;
      });
    return resp;
  }
}

export { SignatureRequestService as default };
